import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchDepartments, createDepartment, updateDepartment, deleteDepartment, fetchEmployees } from '../../../services/employee';
import CustomPagination from '../../../components/common/CustomPagination.js';

const Departments = () => {
  const { t } = useTranslation(['common', 'employees']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    is_active: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    code: '',
    description: '',
    head: '',
    parent_department: '',
    budget_code: '',
    location: 'nigeria',
    is_active: true
  });
  const [employees, setEmployees] = useState([]);
  const [parentDepartments, setParentDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchDepartments(params);
      
      if (response.results) {
        setDepartments(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setDepartments(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch departments');
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch employees and departments for dropdowns when modal opens
  useEffect(() => {
    const fetchDropdownData = async () => {
      if (modalOpen) {
        try {
          const [employeesRes, deptsRes] = await Promise.all([
            fetchEmployees({ page_size: 1000 }).catch(() => ({ results: [] })),
            fetchDepartments({ page_size: 1000 }).catch(() => ({ results: [] }))
          ]);
          
          setEmployees(employeesRes.results || employeesRes || []);
          // Exclude current department from parent departments when editing
          const allDepts = deptsRes.results || deptsRes || [];
          setParentDepartments(editId ? allDepts.filter(d => d.id !== editId) : allDepts);
        } catch (err) {
          console.error('Failed to fetch dropdown data:', err);
        }
      }
    };
    
    fetchDropdownData();
  }, [modalOpen, editId]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const handleOpenModal = (department = null) => {
    if (department) {
      setEditId(department.id);
      setForm({
        name: department.name || '',
        code: department.code || '',
        description: department.description || '',
        head: department.head?.id || department.head || '',
        parent_department: department.parent_department?.id || department.parent_department || '',
        budget_code: department.budget_code || '',
        location: department.location || 'nigeria',
        is_active: department.is_active !== undefined ? department.is_active : true
      });
    } else {
      setEditId(null);
      setForm({
        name: '',
        code: '',
        description: '',
        head: '',
        parent_department: '',
        budget_code: '',
        location: 'nigeria',
        is_active: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm({
      name: '',
      code: '',
      description: '',
      head: '',
      parent_department: '',
      budget_code: '',
      location: 'nigeria',
      is_active: true
    });
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validation
    if (!form.name || !form.name.trim()) {
      setSnackbar({ open: true, message: 'Department name is required', severity: 'error' });
      return;
    }

    if (!form.code || !form.code.trim()) {
      setSnackbar({ open: true, message: 'Department code is required', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        description: form.description || null,
        head: form.head || null,
        parent_department: form.parent_department || null,
        budget_code: form.budget_code || null,
        location: form.location,
        is_active: form.is_active
      };

      if (editId) {
        await updateDepartment(editId, payload);
        setSnackbar({ open: true, message: 'Department updated successfully', severity: 'success' });
      } else {
        await createDepartment(payload);
        setSnackbar({ open: true, message: 'Department created successfully', severity: 'success' });
      }
      
      handleCloseModal();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          Object.values(err.response?.data || {}).flat().join(', ') ||
                          err.message || 
                          'Failed to save department';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return departments.map((dept, index) => ({
      ...dept,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [departments, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('employees:departments.columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'code',
      headerName: t('employees:departments.columns.code'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'name',
      headerName: t('employees:departments.columns.name'),
      flex: 1,
      minWidth: 150,
      sortable: false
    },
    {
      field: 'location',
      headerName: t('employees:departments.columns.location'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const location = params.value || 'nigeria';
        const labels = {
          'nigeria': t('employees:nigeria'),
          'china': t('employees:china'),
          'other': t('employees:other')
        };
        return labels[location] || location;
      }
    },
    {
      field: 'head_name',
      headerName: t('employees:departments.columns.head'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'employees_count',
      headerName: t('employees:departments.columns.employees'),
      width: 100,
      sortable: false,
      renderCell: (params) => params.value || 0
    },
    {
      field: 'is_active',
      headerName: t('employees:departments.columns.status'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('employees:active') : t('employees:inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'created_at',
      headerName: t('employees:departments.columns.created'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    }
  ], [t]);

  if (loading && departments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('employees:departments.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('employees:departments.addDepartment')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('employees:search')}
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder={t('employees:departments.searchPlaceholder')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('employees:location')}
                fullWidth
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <option value="">{t('employees:all')}</option>
                <option value="nigeria">{t('employees:nigeria')}</option>
                <option value="china">{t('employees:china')}</option>
                <option value="other">{t('employees:other')}</option>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('employees:status')}
                fullWidth
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <option value="">{t('employees:all')}</option>
                <option value="true">{t('employees:active')}</option>
                <option value="false">{t('employees:inactive')}</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      {/* Add/Edit Department Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? 'Edit Department' : 'Add Department'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Department Name"
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                  placeholder="e.g., Human Resources"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Department Code"
                  value={form.code}
                  onChange={(e) => handleFormChange('code', e.target.value.toUpperCase())}
                  required
                  InputLabelProps={{ shrink: true }}
                  placeholder="e.g., HR, IT, SALES"
                  helperText="Unique code for the department"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={form.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  multiline
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                  placeholder="Department description..."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label={t('employees:location')}
                  value={form.location}
                  onChange={(e) => handleFormChange('location', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="nigeria">{t('employees:nigeria')}</option>
                  <option value="china">{t('employees:china')}</option>
                  <option value="other">{t('employees:other')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={parentDepartments}
                  getOptionLabel={(option) => `${option.name || ''} (${option.code || ''})`}
                  value={parentDepartments.find(d => d.id === form.parent_department) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('parent_department', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Parent Department"
                      placeholder="Select parent department (optional)"
                      InputLabelProps={{ shrink: true }}
                      helperText="Optional: Select if this is a sub-department"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''} (${option.employee_id || ''})`}
                  value={employees.find(e => e.id === form.head) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('head', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Department Head"
                      placeholder="Select department head (optional)"
                      InputLabelProps={{ shrink: true }}
                      helperText="Optional: Select an employee as department head"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Budget Code"
                  value={form.budget_code}
                  onChange={(e) => handleFormChange('budget_code', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  placeholder="Optional budget code"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label={t('employees:status')}
                  value={form.is_active ? 'true' : 'false'}
                  onChange={(e) => handleFormChange('is_active', e.target.value === 'true')}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="true">{t('employees:active')}</option>
                  <option value="false">{t('employees:inactive')}</option>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            {t('common:buttons.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? t('common:buttons.update') : t('common:buttons.create'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Departments;

